import React, { Suspense, lazy } from 'react'

import { Routes, Route, Navigate, BrowserRouter } from 'react-router-dom'
import { ROUTES, PERMISSIONS } from 'consts'

//base layout
import UI from 'components/Layout/UI'
import Authentication from 'components/authentication'
import SimpleLoading from 'components/loading/SimpleLoading'
import ChunkErrorBoundary from 'components/chunk-error-boundary'

// Critical auth pages - loaded immediately (no lazy loading)
import Login from './login'
import NotFound from './not-found/404'
import Mail from './mail'
import ForgotPassword from './forgot-password'
import NewPassword from './new-password'
import InstallAppEtsy from './install-app-etsy'
import InstallAppAmazon from './install-app-amazon'
import VerifyAppAmazon from './verify-app-amazon'
import AuthApp from './auth-app'
import RedirectApp from './redirect'
import Privacy from './privacy'

// Heavy routes - lazy loaded for performance (code splitting)
const Dashboard = lazy(() => import('./dashboard'))
const Orders = lazy(() => import('./orders'))
const Products = lazy(() => import('./products'))
const AddProduct = lazy(() => import('./products/add'))
const OrdersTracking = lazy(() => import('./orders-tracking'))
const OrdersTrackingDetail = lazy(() => import('./orders-tracking/orders-tracking-detail'))
const Sites = lazy(() => import('./sites'))
const Balance = lazy(() => import('./balance'))
const News = lazy(() => import('./news'))
const NewsLetter = lazy(() => import('./news/newsletter'))
const Users = lazy(() => import('./users'))
const Profile = lazy(() => import('./profile'))
const Roles = lazy(() => import('./roles'))
const SellerTransaction = lazy(() => import('./seller-transaction'))
const SupplierTransaction = lazy(() => import('./supplier-transaction'))
const SettingCategories = lazy(() => import('./setting-categories'))
const SettingCarries = lazy(() => import('./setting-carries'))
const SettingSellerLevels = lazy(() => import('./setting-seller-levels'))
const SettingNews = lazy(() => import('./news'))
const NotificationsCenter = lazy(() => import('./notifications'))
const SettingApp = lazy(() => import('./setting-app'))
const SettingContent = lazy(() => import('./setting-content'))
const ProductTypes = lazy(() => import('./product-types'))
const LogsApp = lazy(() => import('./log'))
const OrderArchive = lazy(() => import('./order-archive'))
const TrackingLog = lazy(() => import('./tracking-log'))
const AccountSetting = lazy(() => import('./account-credit'))
const Disputes = lazy(() => import('./dispute'))
const SkuMapping = lazy(() => import('./sku-mapping'))
const OrderInvoice = lazy(() => import('./order-invoice'))
const OrderInvoiceSources = lazy(() => import('./order-invoice/sources-settings'))
const MoneyFlow = lazy(() => import('./money-flow'))
const MoneyFlowSuppliers = lazy(() => import('./money-flow/suppliers'))
const MoneyFlowPayments = lazy(() => import('./money-flow/payments'))
const MoneyFlowMonthlyReport = lazy(() => import('./money-flow/monthly-report'))
const AmazonDisbursements = lazy(() => import('./money-flow/amazon-disbursements'))
const Analytics = lazy(() => import('./analytics'))
const AmazonBulkAds = lazy(() => import('./ads/amazon-bulk-ads'))
const AmazonAds = lazy(() => import('./ads/amazon-ads'))
const AmazonAdsAutomationRules = lazy(() => import('./ads/amazon-ads-automation-rules'))
const SiteAnalytics = lazy(() => import('./site-analytics'))
const AmazonKeywords = lazy(() => import('./ads/amazon-keywords'))
const InventoryListing = lazy(() => import('./inventory/listing'))
const ListingQueue = lazy(() => import('./inventory/listing-queue'))
const ListingQueueDetail = lazy(() => import('./inventory/listing-queue/detail'))
const AmzProductTypes = lazy(() => import('./inventory/amz-product-types'))
const AmzProductTypeDetail = lazy(() => import('./inventory/amz-product-types/detail'))
const FBAShipments = lazy(() => import('./inventory/fba-shipments'))
const PurchaseOrders = lazy(() => import('./inventory/purchase-orders'))
const PODetail = lazy(() => import('./inventory/purchase-orders/components/PODetail'))
const CreatePOPage = lazy(() => import('./inventory/purchase-orders/components/CreatePOPage'))
const POSettings = lazy(() => import('./setting-po'))
const SettingFulfillment = lazy(() => import('./setting-fulfillment'))
// Research / FB Ads Library
const FacebookAdsResearch = lazy(() => import('./facebook-ads-research'))
const FacebookAdsResearchSettings = lazy(() => import('./facebook-ads-research/settings'))
// Marketing / Ads Command Center (phase-02+)
const MarketingDashboard = lazy(() => import('./fb-ads-marketing'))
const MarketingSettings = lazy(() => import('./fb-ads-marketing/settings'))
const MarketingSoldProducts = lazy(() => import('./fb-ads-marketing/sold-products'))

// Helpdesk (Phase 06)
const HelpdeskInbox = lazy(() => import('./helpdesk/inbox'))
const HelpdeskTicket = lazy(() => import('./helpdesk/ticket-view'))
const HelpdeskSlaSettings = lazy(() => import('./helpdesk/sla-settings'))
const HelpdeskStoreSettings = lazy(() => import('./helpdesk/store-settings'))
const HelpdeskDnsGuide = lazy(() => import('./helpdesk/dns-guide'))
const HelpdeskCannedReplies = lazy(() => import('./helpdesk/canned-replies'))
const HelpdeskBlockedSenders = lazy(() => import('./helpdesk/blocked-senders'))

const DEFINE_ROUTER = [
  {
    path: ROUTES.DASHBOARD,
    Component: () => <Dashboard />,
    title: 'Dashboard',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.ORDERS,
    Component: () => <Orders />,
    title: 'Orders',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.ORDERS_TRACKING,
    Component: () => <OrdersTracking />,
    title: 'Orders tracking',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.PRODUCTS,
    Component: () => <Products />,
    title: 'Products',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.ADD_PRODUCT,
    Component: () => <AddProduct />,
    title: 'Add Product',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.BALANCE,
    Component: () => <Balance />,
    title: 'Balance',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SITES,
    Component: () => <Sites />,
    title: 'Sites',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SELLER_TRANSACTION,
    Component: () => <SellerTransaction />,
    title: 'Seller Transaction',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SUPPLIER_TRANSACTION,
    Component: () => <SupplierTransaction />,
    title: 'Supplier Transaction',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.NOTIFICATIONS,
    Component: () => <NotificationsCenter />,
    title: 'Trung tâm thông báo',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.NEWS,
    Component: () => <News />,
    title: 'News',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.NEWS_LETTER,
    Component: () => <NewsLetter />,
    title: 'NewsLetter',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.USERS,
    Component: () => <Users />,
    title: 'Users',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.ROLES,
    Component: () => <Roles />,
    title: 'Roles',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SETTING_CATEGORIES,
    Component: () => <SettingCategories />,
    title: 'Setting Categories',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SETTING_APP,
    Component: () => <SettingApp />,
    title: 'Setting App',
    permissions: [PERMISSIONS.view_app_setting],
    exact: true,
  },
  {
    path: ROUTES.SETTING_CARRIES,
    Component: () => <SettingCarries />,
    title: 'Setting Carriers',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SETTING_SELLER_LEVELS,
    Component: () => <SettingSellerLevels />,
    title: 'Setting Seller Levels',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SETTING_NEWS,
    Component: () => <SettingNews />,
    title: 'Setting News',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.SETTING_CONTENT,
    Component: () => <SettingContent />,
    title: 'Setting Content',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.ORDERS_TRACKING_DETAIL,
    Component: () => <OrdersTrackingDetail />,
    title: 'Orders Tracking Detail',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.PRODUCT_TYPES,
    Component: () => <ProductTypes />,
    title: 'Product types',
    permissions: [],
    exact: true,
  },
  {
    path: ROUTES.PROFILE,
    Component: () => <Profile />,
    exact: true,
    title: 'Profile',
    permissions: [],
  },
  {
    path: ROUTES.LOGS,
    Component: () => <LogsApp />,
    exact: true,
    title: 'Logs App',
    permissions: [],
  },
  {
    path: ROUTES.ORDER_ARCHIVE,
    Component: () => <OrderArchive />,
    exact: true,
    title: 'Orders Archive',
    permissions: [],
  },
  {
    path: ROUTES.TRACKING_LOG,
    Component: () => <TrackingLog />,
    exact: true,
    title: 'History import tracking number',
    permissions: [],
  },
  // Resolution System Routes
  {
    path: ROUTES.ACCOUNT_SETTING,
    Component: () => <AccountSetting />,
    exact: true,
    title: 'Account Settings',
    permissions: [],
  },
  {
    path: ROUTES.DISPUTES,
    Component: () => <Disputes />,
    exact: true,
    title: 'Disputes',
    permissions: [],
  },
  // Analytics
  {
    path: ROUTES.ANALYTICS,
    Component: () => <Analytics />,
    exact: true,
    title: 'Analytics',
    permissions: [PERMISSIONS.view_analytics],
  },
  // Amazon Bulk Ads
  {
    path: ROUTES.AMAZON_BULK_ADS,
    Component: () => <AmazonBulkAds />,
    exact: true,
    title: 'Amazon Bulk Ads',
    permissions: [],
  },
  // Amazon Ads
  {
    path: ROUTES.SITE_ANALYTICS,
    Component: () => <SiteAnalytics />,
    exact: true,
    title: 'Site Analytics',
    permissions: [],
  },
  {
    path: ROUTES.AMAZON_ADS,
    Component: () => <AmazonAds />,
    exact: true,
    title: 'Amazon Ads',
    permissions: [],
  },
  // Amazon Ads Automation Rules
  {
    path: ROUTES.AMAZON_ADS_AUTOMATION_RULES,
    Component: () => <AmazonAdsAutomationRules />,
    exact: true,
    title: 'AMZ Ads Automation Rules',
    permissions: [PERMISSIONS.view_automation_rules],
  },
  // Amazon Keywords Library
  {
    path: ROUTES.AMAZON_KEYWORDS,
    Component: () => <AmazonKeywords />,
    exact: true,
    title: 'Amazon Keywords Library',
    permissions: [PERMISSIONS.view_keywords],
  },
  // SKU Mapping & COGS System Routes
  {
    path: ROUTES.SKU_MAPPING,
    Component: () => <SkuMapping />,
    exact: true,
    title: 'SKU Mapping',
    permissions: [],
  },
  {
    path: ROUTES.ORDER_INVOICE,
    Component: () => <OrderInvoice />,
    exact: true,
    title: 'Order Invoice',
    permissions: [],
  },
  {
    path: ROUTES.ORDER_INVOICE_SOURCES,
    Component: () => <OrderInvoiceSources />,
    exact: true,
    title: 'Order Invoice Sources & Settings',
    permissions: [],
  },
  {
    path: ROUTES.MONEY_FLOW,
    Component: () => <MoneyFlow />,
    exact: true,
    title: 'Money Flow Overview',
    permissions: [],
  },
  {
    path: ROUTES.MONEY_FLOW_SUPPLIERS,
    Component: () => <MoneyFlowSuppliers />,
    exact: true,
    title: 'Suppliers',
    permissions: [],
  },
  {
    path: ROUTES.MONEY_FLOW_PAYMENTS,
    Component: () => <MoneyFlowPayments />,
    exact: true,
    title: 'Payments',
    permissions: [],
  },
  {
    path: ROUTES.MONEY_FLOW_MONTHLY_REPORT,
    Component: () => <MoneyFlowMonthlyReport />,
    exact: true,
    title: 'Monthly Report',
    permissions: [],
  },
  {
    path: ROUTES.AMAZON_DISBURSEMENTS,
    Component: () => <AmazonDisbursements />,
    exact: true,
    title: 'Amazon Disbursements',
    permissions: [],
  },
  // Inventory
  {
    path: ROUTES.INVENTORY_LISTING,
    Component: () => <InventoryListing />,
    exact: true,
    title: 'Inventory Listing',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_LISTING_QUEUE,
    Component: () => <ListingQueue />,
    exact: true,
    title: 'Listing Queue',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_LISTING_QUEUE_DETAIL,
    Component: () => <ListingQueueDetail />,
    exact: true,
    title: 'Listing Job',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_AMZ_PRODUCT_TYPES,
    Component: () => <AmzProductTypes />,
    exact: true,
    title: 'AMZ Product Types',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_AMZ_PRODUCT_TYPE_DETAIL,
    Component: () => <AmzProductTypeDetail />,
    exact: true,
    title: 'Product Type',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_FBA_SHIPMENTS,
    Component: () => <FBAShipments />,
    exact: true,
    title: 'FBA Shipments',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_PURCHASE_ORDERS,
    Component: () => <PurchaseOrders />,
    exact: true,
    title: 'Purchase Orders',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_PURCHASE_ORDER_CREATE,
    Component: () => <CreatePOPage />,
    exact: true,
    title: 'Create Purchase Order',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_PURCHASE_ORDER_EDIT,
    Component: () => <CreatePOPage />,
    exact: true,
    title: 'Edit Purchase Order',
    permissions: [],
  },
  {
    path: ROUTES.INVENTORY_PURCHASE_ORDER_DETAIL,
    Component: () => <PODetail />,
    exact: true,
    title: 'Purchase Order Detail',
    permissions: [],
  },
  {
    path: ROUTES.SETTING_PO,
    Component: () => <POSettings />,
    exact: true,
    title: 'PO Settings',
    permissions: [],
  },
  {
    path: ROUTES.SETTING_FULFILLMENTS,
    Component: () => <SettingFulfillment />,
    exact: true,
    title: 'Fulfillment Settings',
    permissions: [],
  },
  // Helpdesk (Phase 06)
  {
    path: ROUTES.HELPDESK_INBOX,
    Component: () => <HelpdeskInbox />,
    exact: true,
    title: 'Helpdesk Inbox',
    permissions: [PERMISSIONS.view_helpdesk_inbox],
  },
  {
    path: ROUTES.HELPDESK_TICKET,
    Component: () => <HelpdeskTicket />,
    exact: true,
    title: 'Helpdesk Ticket',
    permissions: [PERMISSIONS.view_helpdesk_ticket],
  },
  {
    path: ROUTES.HELPDESK_SLA_SETTINGS,
    Component: () => <HelpdeskSlaSettings />,
    exact: true,
    title: 'Helpdesk SLA Settings',
    permissions: [PERMISSIONS.manage_helpdesk_settings],
  },
  {
    path: ROUTES.HELPDESK_STORE_SETTINGS,
    Component: () => <HelpdeskStoreSettings />,
    exact: true,
    title: 'Helpdesk Stores',
    permissions: [PERMISSIONS.manage_helpdesk_stores],
  },
  {
    path: ROUTES.HELPDESK_DNS_GUIDE,
    Component: () => <HelpdeskDnsGuide />,
    exact: true,
    title: 'DNS Setup Guide',
    permissions: [PERMISSIONS.manage_helpdesk_stores],
  },
  {
    path: ROUTES.HELPDESK_CANNED_REPLIES,
    Component: () => <HelpdeskCannedReplies />,
    exact: true,
    title: 'Canned Replies',
    permissions: [PERMISSIONS.view_canned_replies],
  },
  {
    path: ROUTES.HELPDESK_BLOCKED_SENDERS,
    Component: () => <HelpdeskBlockedSenders />,
    exact: true,
    title: 'Blocked Senders',
    permissions: [PERMISSIONS.mark_helpdesk_spam],
  },
  // Research / FB Ads Library
  {
    path: ROUTES.RESEARCH_FB_ADS,
    Component: () => <FacebookAdsResearch />,
    exact: true,
    title: 'FB Ads Library Research',
    permissions: [PERMISSIONS.research_fb_ads_search],
  },
  {
    path: ROUTES.RESEARCH_FB_ADS_SETTINGS,
    Component: () => <FacebookAdsResearchSettings />,
    exact: true,
    title: 'FB Ads Token Settings',
    permissions: [PERMISSIONS.research_fb_ads_manage_config],
  },
  // Marketing / Ads Command Center (phase-02+)
  {
    path: ROUTES.MARKETING,
    Component: () => <MarketingDashboard />,
    exact: true,
    title: 'Marketing · Dashboard',
    permissions: [PERMISSIONS.fb_marketing_view],
  },
  {
    path: ROUTES.MARKETING_SETTINGS,
    Component: () => <MarketingSettings />,
    exact: true,
    title: 'Marketing · Settings',
    permissions: [PERMISSIONS.fb_marketing_view],
  },
  {
    path: ROUTES.MARKETING_SOLD_PRODUCTS,
    Component: () => <MarketingSoldProducts />,
    exact: true,
    title: 'Marketing · Sold Products',
    permissions: [PERMISSIONS.fb_marketing_view],
  },
]

const AUTH_ROUTER = [
  {
    path: ROUTES.LOGIN,
    Component: () => <Login />,
    exact: true,
    title: 'Login to fullfillment',
    permissions: [],
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    Component: () => <ForgotPassword />,
    exact: true,
    title: 'Forgot password',
    permissions: [],
  },
  {
    path: ROUTES.NEW_PASSWORD,
    Component: () => <NewPassword />,
    exact: true,
    title: 'New password',
    permissions: [],
  },
  {
    path: ROUTES.MAIL,
    Component: () => <Mail />,
    exact: true,
    title: 'Authentication otp',
    permissions: [],
  },
]

export default function Views() {
  return (
    <BrowserRouter>
      {/* Bắt lỗi tải chunk của bản build cũ -> tự nạp lại thay vì để trắng trang */}
      <ChunkErrorBoundary>
        <Suspense fallback={<SimpleLoading />}>
          <Routes>
          <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />

          {/* <Route path="/" element={<RedirectApp />} /> */}

          <Route path={ROUTES.INSTALL} element={<InstallAppEtsy />} />

          <Route path={ROUTES.AUTH} element={<AuthApp />} />

          <Route path={ROUTES.PRIVACY} element={<Privacy />} />

          <Route path={ROUTES.INSTALL_APP_AMAZON} element={<InstallAppAmazon />} />

          <Route path={ROUTES.VERIFY_APP_AMAZON} element={<VerifyAppAmazon />} />

          {AUTH_ROUTER.map(({ Component, exact, ...rest }, index) => (
            <Route {...rest} key={index} element={<Component />} />
          ))}

          {DEFINE_ROUTER.map(({ Component, exact, ...rest }, index) => (
            <Route
              {...rest}
              key={index}
              element={
                <Authentication {...rest}>
                  <UI>
                    <Component />
                  </UI>
                </Authentication>
              }
            />
          ))}

          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </BrowserRouter>
  )
}
